import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { Participant } from "../types/PersologOnlineTypes.ts";

/**
 * The shared shape of the destructive participant row actions (reset questionnaire, delete, …):
 * a confirmation dialog that names the participant, then the API call with a loading state and a
 * success toast, and a 409 read as "the participant's status forbids it" rather than a failure —
 * the backend's guard, not a broken request.
 *
 * `messages` is the i18n namespace and has to carry: `action` (accept button), `confirm.header`,
 * `confirm.message` (gets `{participant}`), `success` (gets `{participant}`), `notAllowed` (409)
 * and `error`.
 */
export function useConfirmedParticipantAction<T>(options: {
    participant: () => Participant;
    messages: string;
    confirmGroup: () => string;
    logMessage: string;
    action: (participantId: number) => Promise<T>;
    onDone: (result: T) => void;
}) {
    const { t } = useI18n();
    const confirm = useConfirm();
    const toast = useToast();

    const loading = ref(false);

    const participantName = computed(() => {
        const participant = options.participant();
        const name = `${participant.firstName ?? ""} ${participant.name ?? ""}`.trim();
        return name || participant.email;
    });

    const run = async () => {
        const participantId = options.participant().id;
        if (!participantId) return;

        loading.value = true;
        try {
            const result = await options.action(participantId);
            toast.add({
                severity: "success",
                summary: t("messages.success"),
                detail: t(`${options.messages}.success`, { participant: participantName.value }),
                life: 4000,
            });
            options.onDone(result);
        } catch (error) {
            console.error(options.logMessage, error);
            const conflict = (error as { response?: { status?: number } })?.response?.status === 409;
            toast.add({
                severity: "error",
                summary: t("messages.error"),
                detail: conflict ? t(`${options.messages}.notAllowed`) : t(`${options.messages}.error`),
                life: 5000,
            });
        } finally {
            loading.value = false;
        }
    };

    const askForConfirmation = () => {
        confirm.require({
            group: options.confirmGroup(),
            header: t(`${options.messages}.confirm.header`),
            message: t(`${options.messages}.confirm.message`, { participant: participantName.value }),
            icon: "pi pi-exclamation-triangle",
            rejectProps: { label: t("buttons.cancel"), severity: "secondary", outlined: true },
            acceptProps: { label: t(`${options.messages}.action`), severity: "danger" },
            accept: run,
        });
    };

    return { loading, participantName, askForConfirmation };
}
